import type { Question, Step, Lead } from '../types/types';
import { QuestionType, StepType } from '../types/enums';

const filterPostResultsQuestions = async ({
  postResultsQuestions,
  programFlowQuestions,
  requiredFields,
  schema,
}: {
  postResultsQuestions?: Step[];
  programFlowQuestions?: Step[];
  requiredFields?: string[];
  schema?: any;
}) => {
  if (!postResultsQuestions || !requiredFields) {
    return [];
  }
  // Filter PRQ questions by required fields
  const prqQuestionsFiltered = postResultsQuestions.filter(
    (question) =>
      question.leadFieldAssociated &&
      requiredFields.includes(question.leadFieldAssociated as string),
  );

  // Resolve the program-specific flow from the flat `programFlowQuestions`
  // array (from the offering's `programFields` pointer flow).
  const programSteps: Step[] = programFlowQuestions ?? [];

  // Get program specific questions from the flat program flow
  const filteredProgramSpecificQuestions: Question[] = [];
  const programFieldsCovered = new Set<string>(); // Track which fields are covered by the program flow
  programSteps.forEach((step) => {
    if (step.type !== StepType.QUESTION) {
      return;
    }

    const question = step as Question;

    // The step's own leadFieldAssociated replaces the legacy record key as a
    // coverage signal.
    const stepField = question.leadFieldAssociated as string | undefined;
    const stepMatchesRequiredField =
      !!stepField && requiredFields.includes(stepField);

    // Check if any option's leadFieldAssociated matches a required field
    let optionMatchesRequiredField = false;
    const matchedFields = new Set<string>();
    if (question.options) {
      question.options.forEach((option) => {
        if (
          option.leadFieldAssociated &&
          requiredFields.includes(option.leadFieldAssociated)
        ) {
          optionMatchesRequiredField = true;
          matchedFields.add(option.leadFieldAssociated);
        }
      });
    }

    // Include the question if either the step field or its options match
    // required fields
    if (stepMatchesRequiredField || optionMatchesRequiredField) {
      filteredProgramSpecificQuestions.push(question);
      // Mark fields as covered
      if (stepMatchesRequiredField && stepField) {
        programFieldsCovered.add(stepField);
      }
      matchedFields.forEach((field) => programFieldsCovered.add(field));
    }
  });

  // Collect all leadFieldAssociated values from program specific questions.
  // Include both the step's own field and any option-level fields so a program
  // question that covers a field at the step level (no matching option) still
  // dedupes the overlapping PRQ question.
  const programFormLeadFields = new Set<string>();
  filteredProgramSpecificQuestions.forEach((question) => {
    if (question.leadFieldAssociated) {
      programFormLeadFields.add(question.leadFieldAssociated as string);
    }
    if (question.options) {
      question.options.forEach((option) => {
        if (option.leadFieldAssociated) {
          programFormLeadFields.add(option.leadFieldAssociated);
        }
      });
    }
  });

  // Filter PRQ questions to exclude ones that overlap with program specific questions
  const dedupedPrqQuestions = prqQuestionsFiltered.filter(
    (question) =>
      !question.leadFieldAssociated ||
      !programFormLeadFields.has(question.leadFieldAssociated as string),
  );

  // Merge PRQ questions and program specific questions
  // Program specific questions come first (priority)
  const allQuestions = [
    ...filteredProgramSpecificQuestions,
    ...dedupedPrqQuestions,
  ];

  /*
    Filtering logic for programTrack questions:
     -  The question only appears if it's in the schema's required array and the user hasn't already provided this information.
     -  The question's options are filtered to only include the enum values that are valid for the school's program.
  */
  const schemaProgramTrackOptions = schema?.properties?.programTrack?.anyOf
    ?.map((option: any) => option.enum)
    .flat();

  const filteredQuestions = allQuestions.map((question) => {
    // Only process programTrack questions
    if (question?.leadFieldAssociated !== 'programTrack') {
      return question;
    }

    const questionToFilter = question as Question;

    // If no valid schema options or no options to filter, return as-is
    if (
      !schemaProgramTrackOptions ||
      schemaProgramTrackOptions.length === 0 ||
      !questionToFilter.options ||
      !Array.isArray(questionToFilter.options)
    ) {
      return question;
    }

    const validOptionsSet = new Set(schemaProgramTrackOptions);
    const filteredOptions = questionToFilter.options.filter((option) => {
      return option.value && validOptionsSet.has(option.value);
    });

    return {
      ...questionToFilter,
      options: filteredOptions,
    };
  });
  /* End of programTrack question filtering logic */

  // Check for missing required fields and add default questions
  const coveredFields = new Set<string>();

  // Add fields covered by program-specific questions (by step field matching)
  programFieldsCovered.forEach((field) => coveredFields.add(field));

  // Add fields covered by PRQ questions (by leadFieldAssociated)
  filteredQuestions.forEach((question) => {
    if (question?.leadFieldAssociated) {
      coveredFields.add(question.leadFieldAssociated as string);
    }
  });

  const missingFields = requiredFields.filter(
    (field) => !coveredFields.has(field),
  );

  // Add default input questions for missing required fields
  const missingFieldQuestions: Step[] = missingFields.map((field) => ({
    id: `default-${field}`,
    type: StepType.QUESTION,
    questionType: QuestionType.TEXT,
    title: `${field}*`,
    leadFieldAssociated: field as keyof Lead,
    isTerminal: false,
    options: [],
  }));

  return [...filteredQuestions, ...missingFieldQuestions];
};

export default filterPostResultsQuestions;
